export const MAX_IMAGE_EDGE = 2048
export const DOWNSCALE_QUALITY = 0.85

export interface TargetSize {
  width: number
  height: number
  scaled: boolean
}

/** Fit (width, height) inside a square of `maxEdge`, preserving aspect ratio. */
export function computeTargetSize(width: number, height: number, maxEdge = MAX_IMAGE_EDGE): TargetSize {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0, scaled: false }
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height, scaled: false }
  const ratio = maxEdge / longest
  const w = width >= height ? maxEdge : Math.max(1, Math.round(width * ratio))
  const h = height > width ? maxEdge : Math.max(1, Math.round(height * ratio))
  return { width: w, height: h, scaled: true }
}

/** GIFs keep animation, so they are never re-encoded. */
export function canDownscale(contentType: string): boolean {
  return contentType === 'image/jpeg' || contentType === 'image/png' || contentType === 'image/webp'
}

/** JPEG stays JPEG; PNG and WebP become WebP (keeps transparency). */
export function outputTypeFor(contentType: string): 'image/jpeg' | 'image/webp' {
  return contentType === 'image/jpeg' ? 'image/jpeg' : 'image/webp'
}

export function renameForType(fileName: string, type: string): string {
  const ext = type === 'image/jpeg' ? 'jpg' : type === 'image/webp' ? 'webp' : 'png'
  const base = fileName.includes('.') ? fileName.slice(0, fileName.lastIndexOf('.')) : fileName
  return `${base}.${ext}`
}

export interface PreparedImage {
  file: File
  width: number | null
  height: number | null
}

async function canvasToBlob(canvas: HTMLCanvasElement | OffscreenCanvas, type: string): Promise<Blob | null> {
  if ('convertToBlob' in canvas) return canvas.convertToBlob({ type, quality: DOWNSCALE_QUALITY })
  return new Promise((resolve) => canvas.toBlob(resolve, type, DOWNSCALE_QUALITY))
}

/** Read dimensions and, when larger than 2048 px, re-encode a smaller copy. */
export async function prepareImage(file: File, contentType: string): Promise<PreparedImage> {
  if (typeof createImageBitmap !== 'function') return { file, width: null, height: null }
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return { file, width: null, height: null }
  }
  try {
    const target = computeTargetSize(bitmap.width, bitmap.height)
    if (!target.scaled || !canDownscale(contentType)) {
      return { file, width: bitmap.width, height: bitmap.height }
    }
    const canvas: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(target.width, target.height)
        : Object.assign(document.createElement('canvas'), { width: target.width, height: target.height })
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null
    if (!ctx) return { file, width: bitmap.width, height: bitmap.height }
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bitmap, 0, 0, target.width, target.height)
    let type: string = outputTypeFor(contentType)
    let blob = await canvasToBlob(canvas, type)
    if (!blob || blob.type !== type) {
      // Encoder not supported (older Safari for WebP): fall back to JPEG.
      type = 'image/jpeg'
      blob = await canvasToBlob(canvas, type)
    }
    if (!blob || blob.size >= file.size) return { file, width: bitmap.width, height: bitmap.height }
    const resized = new File([blob], renameForType(file.name, type), { type, lastModified: file.lastModified })
    return { file: resized, width: target.width, height: target.height }
  } finally {
    bitmap.close()
  }
}
